import { observer } from 'mobx-react';
import React, { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@wix/design-system';
import { AppContextProvider } from '../../AppLoader';
import { ButtonWithLoader } from '../../ButtonWithLoader/ButtonWithLoader';
import { ThemedText } from '../../ThemedComponents';
import { PhoneNumberFields } from '../PremiumUsers2FaEnforcement/Sms/AddPhoneStepContent';
import { DealerOfferCard, NotNowLink } from './DealerOfferCard';

interface PhoneRedesignProps {
  title: string;
  subtitle: string;
  prompt: string;
  primaryButtonText: string;
  showBackButton: boolean;
  skip?: ReactNode;
}

/**
 * redesigned "enter a phone number" step (wix-2025): the enforcement redesign's
 * country-code + number fields, wired to the post-login `phoneStore`.
 */
const PhoneRedesign: React.FC<PhoneRedesignProps> = observer(
  ({ title, subtitle, prompt, primaryButtonText, showBackButton, skip }) => {
    const {
      rootStore: {
        phoneStore,
        navigationStore,
        displayStore: { preset },
      },
    } = useContext(AppContextProvider);

    return (
      <DealerOfferCard
        title={title}
        subtitle={subtitle}
        showBackButton={showBackButton}
        backButtonCallback={() => {
          phoneStore.clean();
          navigationStore.goBack();
        }}
        primaryButton={
          <ButtonWithLoader
            skin={preset.buttonDesign?.skin ?? 'standard'}
            onClick={() =>
              !phoneStore.isSubmitted && phoneStore.onSendVerificationSms()
            }
            disabled={!phoneStore.phoneNumber.isValid}
            showLoader={phoneStore.isSubmitted}
          >
            {primaryButtonText}
          </ButtonWithLoader>
        }
        skip={skip}
      >
        <Box direction="vertical" width="100%" gap={3}>
          <ThemedText>{prompt}</ThemedText>
          <PhoneNumberFields compact store={phoneStore} />
        </Box>
      </DealerOfferCard>
    );
  },
);

/** Recovery phone Dealer offer: add a recovery phone. */
export const RecoveryPhoneRedesign: React.FC = observer(() => {
  const { t } = useTranslation();
  const {
    rootStore: { phoneStore },
  } = useContext(AppContextProvider);
  return (
    <PhoneRedesign
      title={t('post.login.recovery.phone.title')}
      subtitle={t('post.login.recovery.phone.subtitle')}
      prompt={t('post.login.recovery.phone.input.label')}
      primaryButtonText={t('post.login.next')}
      showBackButton={false}
      skip={<NotNowLink onClick={phoneStore.showLater} />}
    />
  );
});

/** Confirm-details "Edit phone" step: replace the recovery phone. */
export const ConfirmPhoneRedesign: React.FC = () => {
  const { t } = useTranslation();
  return (
    <PhoneRedesign
      title={t('post.login.recovery.change.phone.title')}
      subtitle={t('post.login.recovery.phone.subtitle')}
      prompt={t('post.login.recovery.phone.new.phone.number')}
      primaryButtonText={t('post.login.continue')}
      showBackButton
    />
  );
};
